"use client";
import React, { useState, useEffect } from 'react';
import {
  Receipt,
  Save,
  AlertCircle,
  CheckCircle,
  Percent,
  Tag,
  ReceiptIndianRupee
} from 'lucide-react';

import { useSettings } from '../context/SettingsContext';

const TaxSettingsPage = () => {
  const { getSettings, updateSettings } = useSettings();

  const [settingDta, setsettingDta] = useState({
    id: null,
    title: '',
    tax: ''
  });

  const [taxLoading, setTaxLoading] = useState(false);
  const [taxSaveMessage, setTaxSaveMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      setFetchLoading(true);
      try {
        const res = await getSettings();
        if (res?.status && Array.isArray(res.data) && res.data.length > 0) {
          const setting = res.data[0];
          setsettingDta({
            id: setting.id,
            title: setting.title ?? '',
            tax: setting.tax ?? ''
          });
        } else {
          setErrors({ general: 'Failed to load tax settings' });
        }
      } catch {
        setErrors({ general: 'Failed to load tax settings' });
      } finally {
        setFetchLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handlesettingsSave = async () => {
    setErrors({});
    setTaxSaveMessage('');

    if (!settingDta.id) {
      setErrors({ general: 'Settings ID missing' });
      return;
    }

    if (!settingDta.title || !settingDta.tax) {
      setErrors({ general: 'Title and GST are required' });
      return;
    }

    const taxValue = parseFloat(settingDta.tax);
    if (isNaN(taxValue) || taxValue < 0 || taxValue > 100) {
      setErrors({ tax: 'GST must be a valid percentage between 0 and 100' });
      return;
    }

    setTaxLoading(true);
    try {
      const res = await updateSettings(settingDta.id, {
        title: settingDta.title,
        tax: settingDta.tax,
      });

      if (!res?.status) {
        setErrors({ general: res.message || 'Update failed' });
        return;
      }

      setTaxSaveMessage('Tax settings updated successfully!');
      setTimeout(() => setTaxSaveMessage(''), 3000);
    } finally {
      setTaxLoading(false);
    }
  };

  return (
    <main className="p-4 sm:p-6 max-w-7xl mx-auto" role="main">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
            <ReceiptIndianRupee className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">Tax Settings</h1>
            <p className="text-gray-600 mt-1">Manage your GST and tax configuration</p>
          </div>
        </div>

        {/* Success Message */}
        {taxSaveMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-green-800 font-medium">{taxSaveMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {errors.general && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-red-800 font-medium">{errors.general}</span>
          </div>
        )}
      </header>

      {/* Tax Form */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Tax Configuration</h2>
        <p className="text-sm text-gray-500 mb-6">
          Set your default tax label and rate applied across invoices and orders.
        </p>

        {fetchLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Title Field */}
            <div>
              <label htmlFor="tax-title" className="block text-sm font-medium text-gray-700 mb-2">
                Tax Title
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="tax-title"
                  type="text"
                  value={settingDta.title}
                  onChange={(e) =>
                    setsettingDta(prev => ({ ...prev, title: e.target.value }))
                  }
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                  placeholder="e.g. GST, VAT, Sales Tax"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">This label will appear on invoices and receipts</p>
            </div>

            {/* GST / Rate Field */}
            <div>
              <label htmlFor="tax-rate" className="block text-sm font-medium text-gray-700 mb-2">
                Tax Rate (%)
              </label>
             <div className="relative">
  <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />

  <input
    id="tax-rate"
    type="text"
    inputMode="decimal"
    value={settingDta.tax}
    onChange={(e) => {
      let value = e.target.value;

      // Allow only digits and one decimal point
      if (/^\d*\.?\d*$/.test(value)) {
        // Prevent values greater than 100
        if (value === "" || parseFloat(value) <= 100) {
          setsettingDta((prev) => ({
            ...prev,
            tax: value,
          }));
        }
      }
    }}
    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
      errors.tax ? "border-red-300" : "border-gray-300"
    }`}
    placeholder="e.g. 18"
  />
</div>
              {errors.tax ? (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.tax}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">Enter a value between 0 and 100</p>
              )}
            </div>

            {/* Preview Card */}
            {settingDta.title && settingDta.tax && (
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm font-medium text-teal-800 mb-1">Preview</p>
                <p className="text-sm text-teal-700">
                  Tax label: <strong>{settingDta.title}</strong> — Rate: <strong>{settingDta.tax}%</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end mt-8">
          <button
            type="button"
            onClick={handlesettingsSave}
            disabled={taxLoading || fetchLoading || !settingDta.title || !settingDta.tax}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg 
                      hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed 
                      transition-colors font-medium focus:outline-none focus:ring-2 
                      focus:ring-teal-500 focus:ring-offset-2"
          >
            {taxLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {taxLoading ? 'Saving...' : 'Save Tax '}
          </button>
        </div>
      </section>
    </main>
  );
};

export default TaxSettingsPage;